import * as XLSX from 'xlsx';

/**
 * Export data to XLSX format
 * @param {Array} records - Array of records to export
 * @param {Array} fields - Array of field definitions
 * @param {Object} displayNames - Map of field names to display names
 * @param {string} tableName - Name of the table being exported
 * @param {Object} linkedFieldOptions - Options for linked fields
 * @param {Array} fieldLinks - Field link configuration
 */
export const exportToXLSX = (records, fields, displayNames, tableName, linkedFieldOptions = {}, fieldLinks = []) => {
  if (!records || records.length === 0) {
    alert('No data to export');
    return;
  }

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet([]);
  const wb = XLSX.utils.book_new();

  // Get visible fields (filter out certain system fields)
  const visibleFields = fields.filter(f => {
    // Skip certain system fields
    return !['CreatedDate', 'ModifiedDate', 'DeletedDate'].includes(f.fieldName);
  });

  // Prepare data with formatted headers
  const exportData = records.map(record => {
    const row = {};
    visibleFields.forEach(field => {
      const displayName = displayNames[field.fieldName] || field.fieldName;
      const value = formatExportValue(
        field.fieldName,
        record[field.fieldName],
        field,
        linkedFieldOptions,
        fieldLinks
      );
      row[displayName] = value;
    });
    return row;
  });

  // Create sheet from data
  const worksheet = XLSX.utils.json_to_sheet(exportData);

  // Auto-adjust column widths
  const colWidths = visibleFields.map(field => {
    const displayName = displayNames[field.fieldName] || field.fieldName;
    const maxLength = Math.max(
      displayName.length,
      ...records.map(r => String(formatExportValue(
        field.fieldName,
        r[field.fieldName],
        field,
        linkedFieldOptions,
        fieldLinks
      )).length)
    );
    return { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
  });
  worksheet['!cols'] = colWidths;

  // Add formatting to header row
  const headerRow = visibleFields.map(f => displayNames[f.fieldName] || f.fieldName);
  
  // Style header row (optional - requires pro version for some features)
  // For now, we'll just ensure headers are visible
  if (worksheet['!ref']) {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + '1';
      if (!worksheet[address]) continue;
      worksheet[address].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'FFFFAA00' } },
        alignment: { horizontal: 'center', vertical: 'center' }
      };
    }
  }

  // Append sheet to workbook
  XLSX.utils.book_append_sheet(wb, worksheet, 'Data');

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 10);
  const filename = `${tableName}_export_${timestamp}.xlsx`;

  // Write file
  XLSX.writeFile(wb, filename);
};

/**
 * Format a value for export, handling linked fields and special cases
 */
const formatExportValue = (fieldName, value, field, linkedFieldOptions = {}, fieldLinks = []) => {
  if (value === null || value === undefined) {
    return '';
  }

  // Check if this field is linked
  const linkedField = fieldLinks.find(fl =>
    fl.FieldName && fl.FieldName.toLowerCase() === fieldName.toLowerCase()
  );
  const isLinked = linkedField && (linkedField.IsLinked === 1 || linkedField.IsLinked === true);

  // If linked field, resolve the display value
  if (isLinked) {
    const optionsForField = linkedFieldOptions[fieldName] || linkedFieldOptions[fieldName.toLowerCase()];
    if (optionsForField && Array.isArray(optionsForField)) {
      const matchedOption = optionsForField.find(
        item => String(item.id) === String(value)
      );
      if (matchedOption) {
        return matchedOption.display;
      }
    }
  }

  // Handle boolean/bit fields
  if (field && field.dataType === 'bit') {
    return value === 1 || value === '1' || value === true ? 'Yes' : 'No';
  }

  // Handle date fields
  if (field && (field.dataType === 'date' || field.dataType === 'datetime' || field.dataType === 'datetime2')) {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    if (typeof value === 'string') {
      return new Date(value).toLocaleDateString();
    }
  }

  return String(value);
};

/**
 * Export filtered records to XLSX
 * Useful for exporting search results or specific subset
 */
export const exportFilteredRecords = (
  records,
  fields,
  displayNames,
  tableName,
  linkedFieldOptions = {},
  fieldLinks = [],
  filterCriteria = {}
) => {
  // Create a suffix based on filter criteria
  let filterSuffix = '';
  if (Object.keys(filterCriteria).length > 0) {
    filterSuffix = '_filtered';
  }

  const timestamp = new Date().toISOString().slice(0, 10);
  const ws = XLSX.utils.json_to_sheet([]);
  const wb = XLSX.utils.book_new();

  const visibleFields = fields.filter(f => {
    return !['CreatedDate', 'ModifiedDate', 'DeletedDate'].includes(f.fieldName);
  });

  const exportData = records.map(record => {
    const row = {};
    visibleFields.forEach(field => {
      const displayName = displayNames[field.fieldName] || field.fieldName;
      const value = formatExportValue(
        field.fieldName,
        record[field.fieldName],
        field,
        linkedFieldOptions,
        fieldLinks
      );
      row[displayName] = value;
    });
    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);

  const colWidths = visibleFields.map(field => {
    const displayName = displayNames[field.fieldName] || field.fieldName;
    const maxLength = Math.max(
      displayName.length,
      ...records.map(r => String(formatExportValue(
        field.fieldName,
        r[field.fieldName],
        field,
        linkedFieldOptions,
        fieldLinks
      )).length)
    );
    return { wch: Math.min(maxLength + 2, 50) };
  });
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, worksheet, 'Data');

  const filename = `${tableName}_export${filterSuffix}_${timestamp}.xlsx`;
  XLSX.writeFile(wb, filename);
};
